import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [bootstrap, setBootstrap] = useState(null); // null = vérification en cours
  const [email, setEmail] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const navigate = useNavigate();

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (error) {
      setForgotSent(true); // réponse générique volontaire (anti-énumération)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/bootstrap-status`)
      .then((res) => setBootstrap(res.data))
      .catch(() => setBootstrap({ available: false }));
  }, []);

  const handleBootstrap = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/bootstrap`, {
        email,
        password,
        passwordConfirm: confirm,
      }, { withCredentials: true });
      localStorage.setItem('adminUser', res.data.username);
      toast({ title: 'Compte créé', description: 'Vous êtes connecté. Pensez à noter votre mot de passe.' });
      navigate('/admin/dashboard');
    } catch (error) {
      toast({
        title: 'Création impossible',
        description: error.response?.data?.detail || 'Réessayez',
        variant: 'destructive',
      });
      setBootstrap({ available: error.response?.status !== 403 });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, {
        username,
        password
      }, { withCredentials: true });

      localStorage.setItem('adminUser', response.data.username);

      toast({
        title: "Connexion réussie",
        description: "Bienvenue dans le panneau d'administration"
      });

      navigate('/admin/dashboard');
    } catch (error) {
      toast({
        title: "Erreur de connexion",
        description: error.response?.data?.detail || "Identifiants invalides",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-gray-900 rounded-lg p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full mx-auto mb-4 flex items-center justify-center">
              <span className="text-white font-bold text-2xl">NV</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {bootstrap?.available ? 'Créer le premier administrateur' : (mode === 'forgot' ? 'Mot de passe oublié' : 'Administration')}
            </h1>
            <p className="text-gray-400">PARTY-LED.COM</p>
          </div>

          {bootstrap === null && (
            <p className="text-center text-gray-500 text-sm py-6" data-testid="admin-login-checking">Vérification…</p>
          )}

          {bootstrap?.available && (
            <form onSubmit={handleBootstrap} className="space-y-6" data-testid="admin-bootstrap-form">
              <p className="text-sm text-gray-400 leading-relaxed">
                Aucun compte administrateur n'existe encore sur ce site. Créez le vôtre : il sera le seul, et cet écran
                disparaîtra définitivement ensuite.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Votre email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@votre-domaine.com"
                  required
                  className="bg-gray-800 text-white border-gray-700"
                  data-testid="bootstrap-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Mot de passe (12 caractères minimum)</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  minLength={12}
                  required
                  className="bg-gray-800 text-white border-gray-700"
                  data-testid="bootstrap-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Confirmez le mot de passe</label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••••••"
                  minLength={12}
                  required
                  className="bg-gray-800 text-white border-gray-700"
                  data-testid="bootstrap-confirm-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white py-3"
                data-testid="bootstrap-submit-btn"
              >
                {loading ? 'Création…' : 'Créer mon compte et me connecter'}
              </Button>
            </form>
          )}

          {bootstrap?.available === false && mode === 'login' && (
          <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email ou identifiant
              </label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="votre email ou identifiant"
                required
                className="bg-gray-800 text-white border-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mot de passe
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-gray-800 text-white border-gray-700"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white py-3"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>

            <p className="text-center">
              <button
                type="button"
                onClick={() => { setMode('forgot'); setForgotSent(false); setForgotEmail(username.includes('@') ? username : ''); }}
                className="text-sm text-orange-400 hover:text-orange-300 underline"
                data-testid="forgot-password-link"
              >
                Mot de passe oublié ?
              </button>
            </p>
          </form>
          )}

          {bootstrap?.available === false && mode === 'forgot' && (
          <div className="space-y-6" data-testid="admin-forgot-view">
            {forgotSent ? (
              <div className="text-center space-y-4" data-testid="forgot-sent-message">
                <p className="text-green-400 text-sm leading-relaxed">
                  Si un compte correspond à cet email, un lien de réinitialisation vient d'être envoyé.
                  Vérifiez votre boîte de réception (et les spams). Le lien est valable 45 minutes.
                </p>
              </div>
            ) : (
              <form onSubmit={handleForgot} className="space-y-6" data-testid="forgot-password-form">
                <p className="text-sm text-gray-400 leading-relaxed">
                  Saisissez l'email de votre compte administrateur. Nous vous enverrons un lien pour choisir un nouveau mot de passe.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email du compte</label>
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="vous@votre-domaine.com"
                    required
                    className="bg-gray-800 text-white border-gray-700"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white py-3"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Envoi…' : 'Envoyer le lien de réinitialisation'}
                </Button>
              </form>
            )}
            <p className="text-center">
              <button
                type="button"
                onClick={() => setMode('login')}
                className="text-sm text-orange-400 hover:text-orange-300 underline"
                data-testid="forgot-back-to-login"
              >
                Retour à la connexion
              </button>
            </p>
          </div>
          )}

          <p className="text-center text-gray-500 text-sm mt-6">
            Accès réservé — contactez Nouvelle Vision pour vos identifiants.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;